import React, { useState, useEffect, useRef } from "react";
import { cssImageSet, cssImageSetValue } from "../../utils/media";
import { CONTACT_PHONE_TEL } from "../../config/contactConfig";

/* ============================================================
   SAMONE SOLUTIONS — PRODUCT PAGE
   Rent-agreement registration + property brokerage platform

   UPDATE: full visual system swapped to match the Hospital
   Equipment Management page (ProjectTwoProductDetailPage), so the
   two products now share one consistent design language across
   the site:

     Palette : Off-white #FAF8FC · Lavender Panel #F5EEFA ·
               Deep Purple #3D1259 · Near-black Purple #12061F ·
               Accent Purple #6C0A9F · Slate #5A4E68 · Pink #D946A8
     Type    : IBM Plex Sans (display+body) + IBM Plex Mono (readouts)
     Signature: monitor-style dashboard frame, hoverable cards that
                lift + glow, Label/SectionHeading pattern used on
                every section.

   Content, data and copy are unchanged from the previous version —
   only colors, typography, buttons, cards and hover behavior were
   restyled. Plain <a> tags only (no react-router-dom), per project
   convention.

   MOBILE RESPONSIVENESS:
   The layout breakpoints (hero, dashboard split, steps grid, feature
   grid, FAQ/CTA split) restack at 900/860/760/520px. The
   auto-scrolling feature carousel pauses via touchstart/touchend/
   touchcancel handlers (not just mouseenter/mouseleave, which never
   fire on touchscreens), has -webkit-overflow-scrolling:touch for
   iOS momentum scroll, touchAction:'pan-x' so vertical page scroll
   isn't captured, enlarged arrow-button hit areas, and a small-phone
   breakpoint (<=480px) trimming hero/dashboard vertical space.

   REQUEST A DEMO → EMAIL:
   Both "Request a Demo" CTAs (hero + final CTA) open the person's
   email client via a mailto: link addressed to
   hr@sagveektechnologies.in, with subject/body pre-filled for this
   product (Samone Solutions / Rent Agreement Management).

   COLOR UPDATE:
   Accent color swapped from magenta #C0179E to brand purple
   #6C0A9F across all uses (badge, buttons, icon chips, dashboard
   chrome dots, checklist icons, CTA highlights, hover states), with
   a matching darker hover shade #56087F.

   HERO HEADING LEGIBILITY (this revision):
   The coral/purple accent line of the hero heading — headingLine2,
   "on the record." — sat directly on the hero background photo and
   could get lost depending on what part of the image was behind it.
   Added a thin white text-stroke plus a soft dark text-shadow to that
   span only, so it stays legible against any part of the image while
   keeping its accent color.
   ============================================================ */

const DATA = {
  name: "Samone Solutions",
  badge: "All-in-One Rent Agreement Management Platform",
  headingLine1: "Every agreement,",
  headingLine2: "on the record.",
  description:
    "A comprehensive platform for rent agreement registration and property documentation — from stamp duty calculation to eSign, storage, and anytime retrieval. Secure, compliant, and built for modern property businesses.",

  chips: [
    { label: "OTP Verified" },
    { label: "PDF & eSign" },
    { label: "Admin Dashboard" },
    { label: "Live Tracking" },
  ],

  features: [
    { title: "Agreement Creation", desc: "Create rent agreements with intelligent templates in minutes.", icon: "doc" },
    { title: "Stamp Duty Calculator", desc: "Calculate stamp duty accurately for any state and agreement value.", icon: "calc" },
    { title: "OTP Verification", desc: "Verify parties using secure OTP for complete authentication.", icon: "phone" },
    { title: "eSign & Approvals", desc: "Digitally sign agreements and manage approvals online.", icon: "sign" },
    { title: "Document Management", desc: "Store, organize and retrieve agreements & documents anytime.", icon: "folder" },
    { title: "Reports & Analytics", desc: "Get insights on agreements, revenue, status and expirations.", icon: "chart" },
  ],

  visibilityChecklist: [
    "Live agreement status & tracking",
    "Expiry alerts & reminders",
    "Secure document storage",
    "Role-based access control",
  ],

  testimonial: {
    quote:
      "Sagveek Technologies has simplified our agreement management. Everything we need, in one place, accessible anytime.",
    name: "Ganesh",
    role: "Samone Property Manager",
  },

  steps: [
    { n: "01", title: "Create Agreement", desc: "Select template, add details & property information.", icon: "doc" },
    { n: "02", title: "Verify Parties", desc: "Verify tenant & owner using OTP.", icon: "phone" },
    { n: "03", title: "Calculate & Review", desc: "Auto-calculate stamp duty and review agreement.", icon: "calc" },
    { n: "04", title: "eSign & Approve", desc: "Digitally sign and get approvals.", icon: "sign" },
    { n: "05", title: "Store & Access", desc: "Agreement is stored securely and accessible anytime.", icon: "check" },
  ],

  gridFeatures: [
    { title: "Smart Templates", desc: "State-wise legal templates customizable for your needs.", icon: "doc" },
    { title: "Stamp Duty Calculator", desc: "Accurate calculation for all states & agreement types.", icon: "calc" },
    { title: "eSign & Digital Seal", desc: "Legally valid eSign with digital seal & audit trail.", icon: "sign" },
    { title: "Secure Storage", desc: "Encrypted, access-controlled storage for all your documents.", icon: "folder" },
    { title: "Alerts & Reminders", desc: "Never miss an expiry or important event.", icon: "bell" },
    { title: "Reports & Insights", desc: "Data-driven insights for better decision making.", icon: "chart" },
  ],

  faq: [
    { q: "What is Online Agreement?", a: "A platform combining rent-agreement processing (stamp duty calculation, document custody) with a property listing marketplace." },
    { q: "How is OTP verification done?", a: "Clients verify their mobile number via a one-time password — no password required." },
    { q: "Is eSign legally valid?", a: "Yes. Every eSign carries a digital seal and a full audit trail recognized under applicable e-signature regulations." },
    { q: "Where are documents stored?", a: "Signed PDFs are stored server-side, encrypted, and are retrievable anytime after login." },
    { q: "Can I customize templates?", a: "Yes, templates can be customized per state, agreement type and your organization's requirements." },
  ],

  ctaTitleLine1: "Ready to put every agreement",
  ctaTitleLine2: "on the record?",
  ctaDesc: "Join 50,000+ property professionals who trust Samone to manage agreements smarter.",
};

/* ─── Request-a-Demo mailto link (this product) ───
   Built once and reused by both "Request a Demo" CTAs so the
   subject/body stay in sync if either is ever edited. */
const DEMO_EMAIL = "hr@sagveektechnologies.in";
const DEMO_SUBJECT = "Demo Request - Samone Solutions (Rent Agreement Management)";
const DEMO_BODY = [
  "Hi Sagveek Technologies team,",
  "",
  "I would like to request a demo of Samone Solutions, the rent agreement management platform.",
  "",
  "Please let me know a suitable time to connect.",
  "",
  "Thanks,",
].join("\n");
const DEMO_MAILTO = `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent(DEMO_SUBJECT)}&body=${encodeURIComponent(DEMO_BODY)}`;

/* ─── Tokens (Sagveek brand: off-white / deep-purple / purple accent
       system, matched 1:1 to the Hospital Equipment Management page) ─── */
const C = {
  bg: "#FAF8FC",
  surface: "#FFFFFF",
  raised: "#F5EEFA",
  border: "#E6DCEF",
  muted: "#D3B9E3",
  dimText: "#8A7B96",
  subText: "#5A4E68",
  text: "#2A2033",
  bright: "#170B2E",
  teal: "#3D1259",
  tealD: "#12061F",
  tealGlow: "#3D125914",
  coral: "#6C0A9F",
  coralD: "#56087F",
  amber: "#D946A8",
};

const FF = {
  display: "'IBM Plex Sans', system-ui, sans-serif",
  body: "'IBM Plex Sans', system-ui, sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

/* ─── tiny inline icon set (monoline, no external deps) ─── */
function Icon({ name, size = 20, color = C.teal }) {
  const s = { width: size, height: size, display: "block" };
  const common = { fill: "none", stroke: color, strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "doc":
      return (<svg style={s} viewBox="0 0 24 24" {...common}><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5" /><path d="M9.5 13h5M9.5 16.5h5" /></svg>);
    case "calc":
      return (<svg style={s} viewBox="0 0 24 24" {...common}><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M8 7h8M8 11h1M11.5 11h1M15 11h1M8 14.5h1M11.5 14.5h1M15 14.5h1M8 18h1M11.5 18h1M15 18h1" /></svg>);
    case "phone":
      return (<svg style={s} viewBox="0 0 24 24" {...common}><rect x="7" y="2.5" width="10" height="19" rx="2" /><path d="M11 18h2" /></svg>);
    case "sign":
      return (<svg style={s} viewBox="0 0 24 24" {...common}><path d="M4 19c2-.5 3-2 3.5-4 .6 2 1.7 3 3 3 2 0 2.5-4 4-4 1 0 1 2 3 2 1.2 0 1.7-1 2.5-2" /><path d="M4 21h16" /></svg>);
    case "folder":
      return (<svg style={s} viewBox="0 0 24 24" {...common}><path d="M3 6h6l2 2h10v11H3z" /></svg>);
    case "chart":
      return (<svg style={s} viewBox="0 0 24 24" {...common}><path d="M4 20V10M11 20V4M18 20v-7" /><path d="M3 20h18" /></svg>);
    case "bell":
      return (<svg style={s} viewBox="0 0 24 24" {...common}><path d="M6 17h12l-1.6-2.4A5 5 0 0 1 15.5 11V9a3.5 3.5 0 0 0-7 0v2a5 5 0 0 1-.9 3.6z" /><path d="M10 20a2 2 0 0 0 4 0" /></svg>);
    case "check":
      return (<svg style={s} viewBox="0 0 24 24" {...common}><path d="M5 12.5l4.5 4.5L19 7" /></svg>);
    case "arrowRight":
      return (<svg style={{ width: 14, height: 14 }} viewBox="0 0 24 24" {...common}><path d="M5 12h14M13 6l6 6-6 6" /></svg>);
    case "arrowLeftCircle":
      return (<svg style={s} viewBox="0 0 24 24" {...common}><circle cx="12" cy="12" r="9" /><path d="M13 8l-4 4 4 4" /></svg>);
    case "arrowRightCircle":
      return (<svg style={s} viewBox="0 0 24 24" {...common}><circle cx="12" cy="12" r="9" /><path d="M11 8l4 4-4 4" /></svg>);
    default:
      return null;
  }
}

/* ─── Scroll-reveal hook ─── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function Reveal({ children, delay = 0, style }) {
  const { ref, visible } = useInView();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(14px)",
        transition: `opacity 0.55s ease ${delay}s, transform 0.55s ease ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ─── Shared layout primitives (matched to the Hospital Equipment page) ─── */
function Section({ children, style }) {
  return <section style={{ maxWidth: 1120, margin: "0 auto", padding: "88px 40px", ...style }}>{children}</section>;
}

function Label({ text, light = false }) {
  const color = light ? "#fff" : C.coral;
  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
        gap: 8,
        fontFamily: FF.mono,
        fontSize: 11,
        letterSpacing: "2px",
        color,
        textTransform: "uppercase",
        marginBottom: 16,
      }}
    >
      <div style={{ width: 16, height: 1.5, background: color }} />
      {text}
      <div style={{ width: 16, height: 1.5, background: color }} />
    </div>
  );
}

function SectionHeading({ children, light = false }) {
  return (
    <h2
      style={{
        fontFamily: FF.display,
        fontSize: "clamp(28px, 3.6vw, 42px)",
        fontWeight: 700,
        color: light ? "#fff" : C.bright,
        margin: "0 0 16px",
        letterSpacing: "-1px",
        lineHeight: 1.15,
        textAlign: "left",
      }}
    >
      {children}
    </h2>
  );
}

/* Carousel feature card — icon chip, lift + glow on hover */
function FeatureCard({ f }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.raised : C.surface,
        border: `1px solid ${hov ? C.muted : C.border}`,
        borderRadius: 14,
        padding: "26px 24px",
        minWidth: 230,
        flex: "0 0 auto",
        transition: "all 0.2s ease",
        cursor: "default",
        transform: hov ? "translateY(-3px)" : "none",
        boxShadow: hov ? `0 16px 36px ${C.tealGlow}` : "0 1px 2px rgba(23,11,46,0.04)",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: hov ? C.teal : C.raised,
          color: hov ? "#fff" : C.teal,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 16,
          transition: "all 0.2s ease",
        }}
      >
        <Icon name={f.icon} color={hov ? "#fff" : C.teal} />
      </div>
      <h4 style={{ fontFamily: FF.display, fontSize: 16.5, fontWeight: 600, color: C.bright, margin: "0 0 8px", letterSpacing: "-0.2px" }}>{f.title}</h4>
      <p style={{ fontFamily: FF.body, fontSize: 13.5, color: C.subText, margin: 0, lineHeight: 1.7 }}>{f.desc}</p>
    </div>
  );
}

/* Grid feature tile — used for the "Everything you need" grid */
function GridFeature({ f }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.raised : C.surface,
        border: `1px solid ${hov ? C.muted : C.border}`,
        borderRadius: 14,
        padding: "24px 20px",
        transition: "all 0.2s ease",
        cursor: "default",
        transform: hov ? "translateY(-3px)" : "none",
        boxShadow: hov ? `0 16px 36px ${C.tealGlow}` : "0 1px 2px rgba(23,11,46,0.04)",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: hov ? C.teal : C.raised,
          color: hov ? "#fff" : C.teal,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 14,
          transition: "all 0.2s ease",
        }}
      >
        <Icon name={f.icon} size={18} color={hov ? "#fff" : C.teal} />
      </div>
      <h4 style={{ fontFamily: FF.display, fontSize: 15, fontWeight: 600, color: C.bright, margin: "0 0 6px" }}>{f.title}</h4>
      <p style={{ fontFamily: FF.body, fontSize: 13, color: C.subText, margin: 0, lineHeight: 1.6 }}>{f.desc}</p>
    </div>
  );
}

/* Step item — numbered circle + connecting line, matched to Hospital Equipment "How It Works" */
function StepItem({ s, isLast }) {
  return (
    <div style={{ display: "flex", gap: 20, paddingBottom: isLast ? 0 : 28, position: "relative" }}>
      {!isLast && (
        <div
          style={{
            position: "absolute",
            left: 17,
            top: 36,
            bottom: 0,
            width: 1,
            background: `linear-gradient(to bottom, ${C.teal}55, transparent)`,
          }}
        />
      )}
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          background: C.raised,
          border: `1.5px solid ${C.teal}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          zIndex: 1,
        }}
      >
        <span style={{ fontFamily: FF.mono, fontSize: 10, fontWeight: 600, color: C.teal }}>{s.n}</span>
      </div>
      <div style={{ paddingTop: 6 }}>
        <h4 style={{ fontFamily: FF.display, fontSize: 16.5, fontWeight: 600, color: C.bright, margin: "0 0 5px" }}>{s.title}</h4>
        <p style={{ fontFamily: FF.body, fontSize: 13.5, color: C.subText, margin: 0, lineHeight: 1.7 }}>{s.desc}</p>
      </div>
    </div>
  );
}

/* ─── Page ─── */
const SamoneProductPage = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const carouselRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimeoutRef = useRef(null);
  const carouselFeatures = [...DATA.features, ...DATA.features];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* continuous auto-scroll "carousel" motion — pauses on hover/touch,
     loops seamlessly by relying on the duplicated feature list above */
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    let raf;
    const step = () => {
      if (!pausedRef.current) {
        el.scrollLeft += 0.6;
        const singleSetWidth = el.scrollWidth / 2;
        if (el.scrollLeft >= singleSetWidth) {
          el.scrollLeft -= singleSetWidth;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  const scrollCarousel = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    // pause the auto-scroll rAF loop so the manual scroll isn't
    // immediately overridden on the next frame, then resume shortly after
    pausedRef.current = true;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    el.scrollBy({ left: dir * 260, behavior: "smooth" });
    resumeTimeoutRef.current = setTimeout(() => {
      pausedRef.current = false;
    }, 900);
  };

  // Touch devices never fire mouseenter/mouseleave, so without this the
  // rAF auto-scroll loop above keeps nudging scrollLeft forward while a
  // person is mid-swipe, fighting their finger. Pause on touchstart and
  // give it the same short grace period as the arrow buttons before
  // resuming, rather than resuming the instant the finger lifts.
  const handleTouchStart = () => {
    pausedRef.current = true;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  };
  const handleTouchEnd = () => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      pausedRef.current = false;
    }, 1200);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        .sp-root, .sp-root *{ box-sizing:border-box; }
        .sp-shell{ max-width:1160px; margin:0 auto; padding:0 32px 0; }
        @media(max-width:900px){ .sp-shell{ padding:0 20px; } }
        .sp-hero{
          position:relative;
          overflow:hidden;
          width:100%;
          padding:64px max(44px, calc((100vw - 1160px)/2 + 44px)) 64px max(44px, calc((100vw - 1160px)/2 + 44px));
          background-image:${cssImageSet("/images/samone/5.png")};
          background-size:cover;
          background-position:center;
          background-repeat:no-repeat;
        }
        .sp-hero-overlay{
          position:absolute; inset:0;
          background:linear-gradient(100deg, ${C.tealD}F2 0%, ${C.tealD}D9 32%, ${C.tealD}88 58%, ${C.tealD}33 100%);
        }
        .sp-hero-content{ position:relative; z-index:1; max-width:600px; }
        @media(max-width:900px){
          .sp-hero{ padding:44px 24px; }
          .sp-hero-content{ max-width:100%; }
        }
        @media(max-width:480px){
          .sp-hero{ padding:36px 18px; }
          .sp-chips{ gap:16px 22px; }
        }
        .sp-chips{ display:flex; flex-wrap:wrap; gap:22px; margin-top:36px; }
        .sp-carousel{
          display:flex;
          gap:16px;
          overflow-x:auto;
          padding:4px 2px 10px;
          scrollbar-width:none;
          -webkit-overflow-scrolling:touch;
          touch-action:pan-x;
        }
        .sp-carousel::-webkit-scrollbar{ display:none; }
        .sp-arrow-btn{ padding:7px; margin:-7px; border-radius:50%; -webkit-tap-highlight-color:transparent; }
        .sp-split{ display:grid; grid-template-columns:1fr 1fr; gap:60px 80px; align-items:center; }
        @media(max-width:900px){ .sp-split{ grid-template-columns:1fr; } }
        .sp-dash-frame-body{ min-height:320px; }
        @media(max-width:480px){ .sp-dash-frame-body{ min-height:220px; } }
        .sp-steps-wrap{
          position:relative;
          overflow:hidden;
          width:100%;
          padding:88px max(44px, calc((100vw - 1160px)/2 + 44px));
          background-image:${cssImageSet("/images/samone/7.png")};
          background-size:cover;
          background-position:center;
          background-repeat:no-repeat;
        }
        .sp-steps-overlay{
          position:absolute; inset:0;
          background:${C.bg}F0;
        }
        .sp-steps-inner{ position:relative; z-index:1; max-width:1160px; margin:0 auto; }
        @media(max-width:600px){ .sp-steps-wrap{ padding:56px 24px; } }
        @media(max-width:480px){ .sp-steps-wrap{ padding:44px 18px; } }
        .sp-steps-grid{ display:grid; grid-template-columns:1fr 1fr; gap:16px 40px; }
        @media(max-width:760px){ .sp-steps-grid{ grid-template-columns:1fr; } }
        .sp-grid6{ display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }
        @media(max-width:760px){ .sp-grid6{ grid-template-columns:1fr 1fr; } }
        @media(max-width:520px){ .sp-grid6{ grid-template-columns:1fr; } }
        .sp-faqsplit{ display:grid; grid-template-columns:1fr 1fr; gap:40px; align-items:stretch; }
        @media(max-width:860px){ .sp-faqsplit{ grid-template-columns:1fr; } }
        .sp-faqcta-wrap{
          position:relative;
          overflow:hidden;
          width:100%;
          padding:88px max(44px, calc((100vw - 1160px)/2 + 44px)) 88px max(44px, calc((100vw - 1160px)/2 + 44px));
          background:${C.tealD};
        }
        .sp-faqcta-glow{
          position:absolute; inset:0;
          background:radial-gradient(ellipse at center, ${C.teal}55 0%, transparent 70%);
          pointer-events:none;
        }
        .sp-faqcta-inner{ position:relative; z-index:1; max-width:1160px; margin:0 auto; }
        @media(max-width:600px){ .sp-faqcta-wrap{ padding:52px 24px; } }
        @media(max-width:480px){ .sp-faqcta-wrap{ padding:40px 18px; } }
      `}</style>

      <div className="sp-root" style={{ background: C.bg, color: C.text, fontFamily: FF.body, minHeight: "100vh", overflowX: "hidden" }}>

        {/* ───────────────── HERO (full-bleed) ───────────────── */}
        <div className="sp-hero">
          <div className="sp-hero-overlay" />
          <div style={{ position: "absolute", top: "-10%", right: "-6%", width: 520, height: 520, background: `radial-gradient(circle, ${C.coral}22 0%, transparent 70%)`, pointerEvents: "none" }} />
          <div className="sp-hero-content">
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                border: `1px solid #ffffff33`,
                borderRadius: 100,
                padding: "6px 16px 6px 8px",
                marginBottom: 28,
                background: "#ffffff14",
                backdropFilter: "blur(6px)",
              }}
            >
              <div style={{ background: C.coral, borderRadius: 100, padding: "2px 10px", fontFamily: FF.mono, fontSize: 10, color: "#fff", fontWeight: 600, letterSpacing: "1px" }}>
                RENT AGREEMENTS
              </div>
              <span style={{ fontFamily: FF.body, fontSize: 13, color: "#EAF0EE" }}>{DATA.badge}</span>
            </div>

            <h1
              style={{
                fontFamily: FF.display,
                fontWeight: 700,
                fontSize: "clamp(36px, 5.4vw, 58px)",
                lineHeight: 1.08,
                letterSpacing: "-1.5px",
                color: "#fff",
                margin: "0 0 18px",
              }}
            >
              {DATA.headingLine1}
              <br />
              <span
                style={{
                  color: C.coral,
                  WebkitTextStroke: "0.6px rgba(255,255,255,0.85)",
                  textShadow: "0 1px 12px rgba(0,0,0,0.45)",
                }}
              >
                {DATA.headingLine2}
              </span>
            </h1>

            <p style={{ fontFamily: FF.body, fontSize: "clamp(15px, 1.6vw, 17px)", color: "#D7DEE9", maxWidth: 480, margin: "0 0 36px", lineHeight: 1.75 }}>
              {DATA.description}
            </p>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <a
                href={DEMO_MAILTO}
                style={{
                  fontFamily: FF.body,
                  fontWeight: 600,
                  fontSize: 14.5,
                  padding: "15px 30px",
                  background: C.coral,
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: 10,
                  transition: "all 0.2s",
                  display: "inline-block",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = C.coralD)}
                onMouseLeave={(e) => (e.currentTarget.style.background = C.coral)}
              >
                Request a Demo →
              </a>
              <a
                href={`tel:${CONTACT_PHONE_TEL}`}
                style={{
                  fontFamily: FF.body,
                  fontWeight: 600,
                  fontSize: 14.5,
                  padding: "15px 30px",
                  border: `1.5px solid #ffffff55`,
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: 10,
                  transition: "all 0.2s",
                  display: "inline-block",
                  background: "transparent",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#ffffff14"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                Call Us
              </a>
            </div>

            <div className="sp-chips">
              {DATA.chips.map((c, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.coral }} />
                  <span style={{ fontFamily: FF.body, fontSize: 12.5, color: "#C7D0DC" }}>{c.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="sp-shell">

          {/* ───────────────── FEATURE CAROUSEL ───────────────── */}
          <div style={{ paddingTop: 72 }}>
            <div style={{ marginBottom: 44 }}>
              <Label text="Features" />
              <SectionHeading>Powerful features, built for property professionals.</SectionHeading>
            </div>
            <Reveal>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button
                  type="button"
                  className="sp-arrow-btn"
                  onClick={() => scrollCarousel(-1)}
                  aria-label="Scroll left"
                  style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0 }}
                >
                  <Icon name="arrowLeftCircle" size={30} color={C.dimText} />
                </button>
                <div
                  className="sp-carousel"
                  ref={carouselRef}
                  onMouseEnter={() => (pausedRef.current = true)}
                  onMouseLeave={() => (pausedRef.current = false)}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchEnd}
                >
                  {carouselFeatures.map((f, i) => (
                    <FeatureCard key={i} f={f} />
                  ))}
                </div>
                <button
                  type="button"
                  className="sp-arrow-btn"
                  onClick={() => scrollCarousel(1)}
                  aria-label="Scroll right"
                  style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0 }}
                >
                  <Icon name="arrowRightCircle" size={30} color={C.dimText} />
                </button>
              </div>
            </Reveal>
          </div>

          {/* ───────────────── DASHBOARD SPLIT ───────────────── */}
          <div style={{ paddingTop: 104 }}>
            <div className="sp-split">
              {/* Dashboard visual — browser-chrome frame, matched to Hospital Equipment "Live Tracking" */}
              <Reveal>
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, overflow: "hidden", boxShadow: `0 30px 70px ${C.tealGlow}` }}>
                  <div style={{ padding: "13px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8, background: C.tealD }}>
                    {["#6C0A9F", "#8B2FC9", "#3D1259"].map((c) => (
                      <div key={c} style={{ width: 9, height: 9, borderRadius: "50%", background: c }} />
                    ))}
                    <span style={{ fontFamily: FF.mono, fontSize: 10.5, color: "#F5EEFAAA", marginLeft: 8, letterSpacing: "1px" }}>AGREEMENT DASHBOARD</span>
                  </div>
                  <div className="sp-dash-frame-body" style={{ background: C.raised, backgroundImage: cssImageSetValue("/images/samone/4.png"), backgroundSize: "cover", backgroundPosition: "center" }} />
                </div>
              </Reveal>

              <Reveal delay={0.08}>
                <div>
                  <Label text="Live Tracking" />
                  <SectionHeading>Real-time visibility. Total control.</SectionHeading>
                  <p style={{ fontSize: 15, color: C.subText, lineHeight: 1.8, margin: "0 0 26px" }}>
                    Agreements move between owners, tenants and paperwork. Samone keeps every stage — from OTP verification to eSign — visible
                    in one live view, so nothing slips through.
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 26 }}>
                    {DATA.visibilityChecklist.map((item, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, color: C.text }}>
                        <div style={{ width: 22, height: 22, borderRadius: 7, background: C.raised, color: C.teal, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Icon name="check" size={12} />
                        </div>
                        {item}
                      </div>
                    ))}
                  </div>
                  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "22px 24px", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: -14, right: 18, fontFamily: FF.display, fontSize: 80, color: `${C.teal}0E`, lineHeight: 1, pointerEvents: "none", userSelect: "none" }}>❝</div>
                    <p style={{ fontFamily: FF.body, fontStyle: "italic", fontSize: 13.5, color: C.subText, lineHeight: 1.7, margin: "0 0 14px", position: "relative" }}>
                      "{DATA.testimonial.quote}"
                    </p>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, ${C.teal}33, ${C.coral}33)`,
                          border: `1px solid ${C.border}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: FF.display,
                          fontSize: 12,
                          fontWeight: 700,
                          color: C.teal,
                        }}
                      >
                        {DATA.testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontFamily: FF.body, fontWeight: 600, fontSize: 13, color: C.bright }}>{DATA.testimonial.name}</div>
                        <div style={{ fontFamily: FF.mono, fontSize: 10, color: C.dimText, letterSpacing: "0.5px", marginTop: 2 }}>{DATA.testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

        </div>

        {/* ───────────────── 5 STEPS (full-bleed) ───────────────── */}
        <div style={{ marginTop: 104 }}>
          <div className="sp-steps-wrap">
            <div className="sp-steps-overlay" />
            <div className="sp-steps-inner">
              <div style={{ marginBottom: 48 }}>
                <Label text="How It Works" />
                <SectionHeading>Five simple steps to manage your agreements.</SectionHeading>
              </div>
              <Reveal>
                <div className="sp-steps-grid">
                  {DATA.steps.map((s, i) => (
                    <StepItem key={i} s={s} isLast={i === DATA.steps.length - 1} />
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        <div className="sp-shell">

          {/* ───────────────── FEATURE GRID ───────────────── */}
          <div style={{ paddingTop: 104 }}>
            <div style={{ marginBottom: 44 }}>
              <Label text="Everything Included" />
              <SectionHeading>Everything you need for complete agreement management.</SectionHeading>
            </div>
            <div className="sp-grid6">
              {DATA.gridFeatures.map((f, i) => (
                <Reveal key={i} delay={i * 0.03}>
                  <GridFeature f={f} />
                </Reveal>
              ))}
            </div>
          </div>

        </div>

        {/* ───────────────── FAQ + CTA (full-bleed, dark) ───────────────── */}
        <div style={{ marginTop: 96 }}>
          <div className="sp-faqcta-wrap">
            <div className="sp-faqcta-glow" />
            <div className="sp-faqcta-inner sp-faqsplit">
              <Reveal>
                <div style={{ padding: "8px 4px", height: "100%" }}>
                  <Label text="FAQ" light />
                  <SectionHeading light>Common questions.</SectionHeading>
                  <div>
                    {DATA.faq.map((f, i) => {
                      const open = openFaq === i;
                      return (
                        <div key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.16)" }}>
                          <div
                            onClick={() => setOpenFaq(open ? -1 : i)}
                            style={{ padding: "16px 4px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, cursor: "pointer" }}
                          >
                            <span style={{ fontSize: 14.5, fontWeight: 600, color: "#fff" }}>{f.q}</span>
                            <div
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                border: `1px solid ${open ? C.coral : "rgba(255,255,255,0.35)"}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: open ? C.coral : "rgba(255,255,255,0.6)",
                                fontSize: 13,
                                flexShrink: 0,
                                transition: "all 0.2s",
                                transform: open ? "rotate(45deg)" : "none",
                              }}
                            >
                              +
                            </div>
                          </div>
                          {open && (
                            <div style={{ padding: "0 4px 16px", fontSize: 13.5, color: "rgba(255,255,255,0.72)", lineHeight: 1.7 }}>{f.a}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.08}>
                <div
                  style={{
                    padding: "8px 4px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Label text="Get Started" light />
                  <SectionHeading light>
                    {DATA.ctaTitleLine1}
                    <br />
                    <span style={{ color: C.coral }}>{DATA.ctaTitleLine2}</span>
                  </SectionHeading>
                  <p style={{ fontSize: 14.5, color: "#D7DEE9", lineHeight: 1.7, margin: "0 0 30px", maxWidth: 400 }}>{DATA.ctaDesc}</p>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                    <a
                      href={DEMO_MAILTO}
                      style={{
                        fontFamily: FF.body,
                        fontWeight: 600,
                        fontSize: 14.5,
                        padding: "14px 28px",
                        borderRadius: 10,
                        background: C.coral,
                        color: "#fff",
                        textDecoration: "none",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = C.coralD; e.currentTarget.style.transform = "translateY(-2px)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = C.coral; e.currentTarget.style.transform = "none"; }}
                    >
                      Request a Demo →
                    </a>
                    <a
                      href={`tel:${CONTACT_PHONE_TEL}`}
                      style={{
                        fontFamily: FF.body,
                        fontWeight: 500,
                        fontSize: 14.5,
                        padding: "14px 28px",
                        borderRadius: 10,
                        border: "1px solid rgba(255,255,255,0.35)",
                        color: "#fff",
                        textDecoration: "none",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.7)")}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)")}
                    >
                      📞 Call Us Now
                    </a>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default SamoneProductPage;